/**
 * The rules that apply (plan E05-T05): a compliance profile as it applies to this tenant, section
 * by section: each rule, what the tenant changed, what it may not change, and where each rule comes
 * from (its sources, tagged as research 09 tags them). On the tenant's own profile, someone who may
 * change the rules gets "Your changes" (V1-E00-T02).
 */
import { useId, useState } from "react";
import type { Schemas } from "../api/client";
import { Section, useApiQuery } from "../app/Page";
import { useCan } from "../app/session";
import { useT, type MessageId } from "../i18n";
import { YourChanges } from "./YourChanges";

type Effective = Schemas["EffectiveProfile"];
type TenantMay = "none" | "narrow" | "override";

interface Group {
  id: string; // calling_window, disclosure.ai_announcement, ...
  tenantMay: TenantMay;
  rows: { key: string; value: unknown; base: unknown }[];
}

/** The sections of a profile, the two announcements as their own groups (each has its own bounds). */
export function groups(effective: Effective): Group[] {
  const spec = effective.effective as unknown as Record<string, Record<string, unknown>>;
  const base = effective.profile.spec as unknown as Record<string, Record<string, unknown>> | undefined;
  const out: Group[] = [];
  const add = (id: string, now: Record<string, unknown>, was: Record<string, unknown> | undefined) => {
    const rows = Object.entries(now)
      .filter(([k]) => k !== "tenant_may")
      .map(([k, value]) => ({ key: `${id}.${k}`, value, base: was?.[k] }));
    out.push({ id, tenantMay: (now.tenant_may as TenantMay | undefined) ?? "narrow", rows });
  };
  for (const [section, body] of Object.entries(spec)) {
    if (section === "disclosure") {
      for (const [name, item] of Object.entries(body)) {
        add(`disclosure.${name}`, item as Record<string, unknown>, base?.disclosure?.[name] as Record<string, unknown>);
      }
    } else {
      add(section, body, base?.[section]);
    }
  }
  return out;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function Value({ value }: { value: unknown }) {
  const t = useT();
  if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
    return <span className="text-[var(--color-muted)]">{t("compliance.rules.none")}</span>;
  }
  if (typeof value === "boolean") return <span>{t(value ? "compliance.rules.yes" : "compliance.rules.no")}</span>;
  if (Array.isArray(value) && value.every((v) => typeof v !== "object")) return <span className="font-mono">{value.join(", ")}</span>;
  if (typeof value === "object") {
    return <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs">{JSON.stringify(value, null, 2)}</pre>;
  }
  return <span className="font-mono">{String(value)}</span>;
}

const TAGS = ["V", "S", "I", "P", "R"] as const;

/** A source line, its research-09 tag ([V] primary, [S] secondary, ...) shown as a labelled badge. */
export function Source({ text }: { text: string }) {
  const t = useT();
  const tag = TAGS.find((x) => text.startsWith(`[${x}]`));
  const rest = tag ? text.slice(3).trim() : text;
  return (
    <li className="flex gap-2">
      {tag && (
        <span className="h-fit shrink-0 rounded border border-[var(--color-border)] px-1 font-mono text-xs"
          title={t(`compliance.tag.${tag}` as MessageId)}>
          <span aria-hidden="true">{tag}</span>
          <span className="sr-only">{t(`compliance.tag.${tag}` as MessageId)}</span>
        </span>
      )}
      <span>{rest}</span>
    </li>
  );
}

/** Where a rule comes from, folded away until asked for. */
export function SourceList({ sources }: { sources: string[] }) {
  const t = useT();
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-[var(--color-primary)]">{t("compliance.rules.sources", { n: sources.length })}</summary>
      <ul className="mt-1 space-y-1">{sources.map((s) => <Source key={s} text={s} />)}</ul>
    </details>
  );
}

function GroupView({ group, sources }: { group: Group; sources: Record<string, string[]> }) {
  const t = useT();
  const changed = group.rows.some((r) => !same(r.value, r.base));
  const whole = sources[group.id] ?? []; // an announcement's sources are for all of it
  return (
    <section aria-labelledby={`rule-${group.id}`} className="rounded-lg border border-[var(--color-border)] p-4">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <h3 id={`rule-${group.id}`} className="font-mono font-semibold">{group.id}</h3>
        <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-xs">
          {t(`compliance.may.${group.tenantMay}` as MessageId)}
        </span>
        {changed && (
          <span className="rounded-full bg-[var(--color-primary)] px-2 py-0.5 text-xs text-[var(--color-on-primary)]">
            {t("compliance.rules.changed")}
          </span>
        )}
      </header>
      {whole.length > 0 && <SourceList sources={whole} />}
      <dl className="space-y-3">
        {group.rows.map((row) => {
          const own = sources[row.key] ?? [];
          const differs = !same(row.value, row.base);
          return (
            <div key={row.key} className="grid gap-1 md:grid-cols-[14rem_minmax(0,1fr)]">
              <dt className="font-mono text-sm text-[var(--color-muted)]">{row.key.slice(group.id.length + 1)}</dt>
              <dd className="space-y-1 text-sm">
                <Value value={row.value} />
                {differs && (
                  <div className="text-xs text-[var(--color-muted)]">
                    {t("compliance.rules.profileSays")} <Value value={row.base} />
                  </div>
                )}
                {own.length > 0 && <SourceList sources={own} />}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

export function Rules() {
  const t = useT();
  const pickId = useId();
  const [profile, setProfile] = useState<string>("");
  const canChange = useCan("compliance:write");
  const profiles = useApiQuery("compliance-profiles", (api) => api.GET("/v1/compliance/profiles"));
  const effective = useApiQuery(`compliance-effective:${profile}`, (api) =>
    api.GET("/v1/compliance/effective", { params: { query: profile ? { profile } : {} } }),
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label htmlFor={pickId} className="block text-sm font-medium">{t("compliance.rules.profile")}</label>
          <select id={pickId} value={profile} onChange={(e) => setProfile(e.target.value)}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm">
            <option value="">{t("compliance.rules.yours")}</option>
            {(profiles.data?.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{`${p.name} (${p.id})`}</option>
            ))}
          </select>
        </div>
      </div>
      <Section query={effective}>
        {(e) => (
          <div className="space-y-4">
            <p className="text-sm">
              {t(`compliance.chosen.${e.chosen_by}` as MessageId, {
                name: e.profile.metadata.name, id: e.profile.metadata.id, version: e.profile.metadata.version,
              })}
              {" "}
              {e.overrides_hash
                ? t("compliance.rules.overridden", { hash: e.overrides_hash })
                : t("compliance.rules.notOverridden")}
            </p>
            {e.refused && (
              <p role="alert" className="rounded-md border border-[var(--color-danger)] p-3 text-sm">
                {t("compliance.rules.refused", { why: e.refused })}
              </p>
            )}
            <p className="text-xs text-[var(--color-muted)]">{t("compliance.rules.notAdvice")}</p>
            {!profile && canChange && <YourChanges key={e.overrides_hash ?? "none"} effective={e} />}
            {groups(e).map((g) => <GroupView key={g.id} group={g} sources={e.profile.sources ?? {}} />)}
          </div>
        )}
      </Section>
    </div>
  );
}
